function makeHtmlMask(text: string) {
  const mask = new Array(text.length).fill(0);
  let inside = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === "<") {
      inside = true;
      mask[i] = 1;
    } else if (char === ">") {
      mask[i] = 1;
      inside = false;
    } else if (inside) {
      mask[i] = 1;
    }
  }

  return mask;
}

function isOutsideHtml(mask: number[], start: number, end: number) {
  for (let i = start; i < end; i++) {
    if (mask[i] === 1) return false;
  }
  return true;
}

const STOPWORDS = new Set([
  "a",
  "o",
  "as",
  "os",
  "de",
  "da",
  "do",
  "das",
  "dos",
  "em",
  "no",
  "na",
  "nos",
  "nas",
  "para",
  "por",
  "com",
  "sem",
  "e",
  "ou",
  "mas",
  "um",
  "uma",
]);

export function getHighlightedExcerpts(
  text: string,
  searchTerm?: string,
  contextSize = 70,
  returnNullIfNotFound = true,
) {
  if (!searchTerm) return [text.slice(0, 500)];

  const normalize = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const normalizedText = normalize(text);

  const quotedMatches = searchTerm.match(/"(.*?)"/g) || [];
  const quotedPhrases = quotedMatches.map((q) => ({
    raw: q.replace(/"/g, ""),
    normalized: normalize(q.replace(/"/g, "")),
  }));

  const restQuery = searchTerm
    .replace(/"(.*?)"/g, "")
    .trim()
    .split(/\s+/)
    .filter((w) => w && !STOPWORDS.has(w.toLowerCase()));

  let otherTerms: string[] = [];

  if (restQuery.length > 0) {
    otherTerms = normalize(restQuery.join(" "))
      .split(/\s+/)
      .filter((w) => w && !STOPWORDS.has(w));
  }

  let baseIndex: number;

  if (quotedPhrases.length > 0) {
    const firstPhrase = quotedPhrases[0];

    if (firstPhrase) {
      baseIndex = normalizedText.indexOf(firstPhrase.normalized);
    } else {
      baseIndex = -1;
    }

    if (baseIndex === -1) {
      for (const phrase of quotedPhrases) {
        const idx = normalizedText.indexOf(phrase.normalized);
        if (idx !== -1) {
          baseIndex = idx;
          break;
        }
      }
      if (baseIndex === -1) {
        quotedPhrases.length = 0;
      }
    }
  } else {
    baseIndex = Infinity;
    for (const w of otherTerms) {
      const idx = normalizedText.indexOf(w);
      if (idx !== -1) baseIndex = Math.min(baseIndex, idx);
    }
    if (baseIndex === Infinity) {
      if (returnNullIfNotFound) return null;
      return [text];
    }
  }

  const start = Math.max(0, baseIndex - contextSize);
  const end = Math.min(text.length, baseIndex + contextSize * 3);

  let excerpt = text.slice(start, end);
  let normalizedExcerpt = normalize(excerpt);

  if (quotedPhrases.length > 0) {
    for (const phrase of quotedPhrases) {
      const idx = normalizedExcerpt.indexOf(phrase.normalized);
      if (idx !== -1) {
        excerpt =
          excerpt.slice(0, idx) +
          `<mark style="background-color:#f0eee6;font-weight:700;padding:0;">` +
          excerpt.slice(idx, idx + phrase.raw.length) +
          `</mark>` +
          excerpt.slice(idx + phrase.raw.length);

        normalizedExcerpt = normalize(excerpt);
      }
    }
  }

  normalizedExcerpt = normalize(excerpt);
  const mask = makeHtmlMask(excerpt);

  if (otherTerms.length > 0) {
    let ranges: { start: number; end: number }[] = [];

    for (const term of otherTerms) {
      const normTerm = term;

      let index = 0;

      while (true) {
        const found = normalizedExcerpt.indexOf(normTerm, index);
        if (found === -1) break;

        const realStart = found;
        const realEnd = found + term.length;

        if (isOutsideHtml(mask, realStart, realEnd)) {
          ranges.push({ start: realStart, end: realEnd });
        }

        index = found + term.length;
      }
    }

    ranges.sort((a, b) => a.start - b.start);

    const merged: { start: number; end: number }[] = [];

    for (const r of ranges) {
      const last = merged[merged.length - 1];

      if (!last || r.start > last.end) {
        merged.push({ ...r });
      } else {
        last.end = Math.max(last.end, r.end);
      }
    }

    let final = "";
    let lastIdx = 0;

    for (const r of merged) {
      final += excerpt.slice(lastIdx, r.start);
      final += `<mark style="background-color:#f0eee6;font-weight:700;padding:0;">${excerpt.slice(
        r.start,
        r.end,
      )}</mark>`;
      lastIdx = r.end;
    }

    final += excerpt.slice(lastIdx);
    excerpt = final;
  }

  return [(start > 0 ? "…" : "") + excerpt + (end < text.length ? "…" : "")];
}

export function highlightFullText(text: string, searchTerm?: string): string {
  if (!searchTerm || !text) return text;

  const normalize = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const quotedMatches = searchTerm.match(/"(.*?)"/g) || [];
  const quotedPhrases = quotedMatches.map((q) => ({
    raw: q.replace(/"/g, ""),
    normalized: normalize(q.replace(/"/g, "")),
  }));

  const restQuery = searchTerm
    .replace(/"(.*?)"/g, "")
    .trim()
    .split(/\s+/)
    .filter((w) => w && !STOPWORDS.has(w.toLowerCase()));

  let otherTerms: string[] = [];

  if (restQuery.length > 0) {
    otherTerms = normalize(restQuery.join(" "))
      .split(/\s+/)
      .filter((w) => w && !STOPWORDS.has(w));
  }

  const normalizedText = normalize(text);
  const mask = makeHtmlMask(text);

  let ranges: { start: number; end: number }[] = [];

  if (quotedPhrases.length > 0) {
    for (const phrase of quotedPhrases) {
      let index = 0;

      while (true) {
        const found = normalizedText.indexOf(phrase.normalized, index);
        if (found === -1) break;

        const end = found + phrase.raw.length;

        if (isOutsideHtml(mask, found, end)) {
          ranges.push({ start: found, end });
        }

        index = end;
      }
    }
  }

  for (const term of otherTerms) {
    let index = 0;

    while (true) {
      const found = normalizedText.indexOf(term, index);
      if (found === -1) break;

      const end = found + term.length;

      if (isOutsideHtml(mask, found, end)) {
        ranges.push({ start: found, end });
      }

      index = end;
    }
  }

  if (ranges.length === 0) return text;

  ranges.sort((a, b) => a.start - b.start);

  const merged: { start: number; end: number }[] = [];

  for (const r of ranges) {
    const last = merged[merged.length - 1];

    if (!last || r.start > last.end) {
      merged.push({ ...r });
    } else {
      last.end = Math.max(last.end, r.end);
    }
  }

  let result = "";
  let lastIndex = 0;

  for (const r of merged) {
    result += text.slice(lastIndex, r.start);
    result += `<mark style="background-color:#f0eee6;font-weight:700;padding:0;">${text.slice(
      r.start,
      r.end,
    )}</mark>`;
    lastIndex = r.end;
  }

  result += text.slice(lastIndex);

  return result;
}
